import type {
  ThemeData,
  ThemeConfig,
  ThemeEntities,
  ThemeBalance,
  ThemeNames,
  ProjectSize,
} from '../types';

export class ThemeValidationError extends Error {
  constructor(
    public field: string,
    message: string,
  ) {
    super(`Theme validation error [${field}]: ${message}`);
    this.name = 'ThemeValidationError';
  }
}

/**
 * Loads and validates theme data from JSON objects.
 * In production, JSON files are imported at build time via Vite.
 * This loader validates the structure and catches missing/invalid fields early.
 */
export function loadTheme(
  config: unknown,
  entities: unknown,
  balance: unknown,
  names: unknown,
  strings: unknown,
): ThemeData {
  const validatedConfig = validateConfig(config);
  const validatedEntities = validateEntities(entities);
  const validatedBalance = validateBalance(balance);
  const validatedNames = validateNames(names);
  const validatedStrings = validateStrings(strings);

  return {
    config: validatedConfig,
    entities: validatedEntities,
    balance: validatedBalance,
    names: validatedNames,
    strings: validatedStrings,
  };
}

function requireField(obj: Record<string, unknown>, field: string, context: string): unknown {
  if (!(field in obj) || obj[field] === undefined || obj[field] === null) {
    throw new ThemeValidationError(context, `Missing required field '${field}'`);
  }
  return obj[field];
}

function requireString(obj: Record<string, unknown>, field: string, context: string): string {
  const value = requireField(obj, field, context);
  if (typeof value !== 'string') {
    throw new ThemeValidationError(context, `'${field}' must be a string, got ${typeof value}`);
  }
  return value;
}

function requireNumber(obj: Record<string, unknown>, field: string, context: string): number {
  const value = requireField(obj, field, context);
  if (typeof value !== 'number' || isNaN(value)) {
    throw new ThemeValidationError(context, `'${field}' must be a number`);
  }
  return value;
}

function requireArray(obj: Record<string, unknown>, field: string, context: string): unknown[] {
  const value = requireField(obj, field, context);
  if (!Array.isArray(value)) {
    throw new ThemeValidationError(context, `'${field}' must be an array`);
  }
  return value;
}

function requireObject(
  obj: Record<string, unknown>,
  field: string,
  context: string,
): Record<string, unknown> {
  const value = requireField(obj, field, context);
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ThemeValidationError(context, `'${field}' must be an object`);
  }
  return value as Record<string, unknown>;
}

function validateConfig(raw: unknown): ThemeConfig {
  if (typeof raw !== 'object' || raw === null) {
    throw new ThemeValidationError('config', 'Config must be an object');
  }
  const obj = raw as Record<string, unknown>;
  return {
    id: requireString(obj, 'id', 'config'),
    name: requireString(obj, 'name', 'config'),
    description: requireString(obj, 'description', 'config'),
    version: requireString(obj, 'version', 'config'),
  };
}

function validateEntities(raw: unknown): ThemeEntities {
  if (typeof raw !== 'object' || raw === null) {
    throw new ThemeValidationError('entities', 'Entities must be an object');
  }
  const obj = raw as Record<string, unknown>;

  const staffRoles = requireArray(obj, 'staffRoles', 'entities');
  if (staffRoles.length === 0) {
    throw new ThemeValidationError('entities.staffRoles', 'Must have at least one staff role');
  }

  const founderRole = requireObject(obj, 'founderRole', 'entities');

  const projectCategories = requireArray(obj, 'projectCategories', 'entities');
  if (projectCategories.length === 0) {
    throw new ThemeValidationError(
      'entities.projectCategories',
      'Must have at least one category',
    );
  }

  const projectSubcategories = requireArray(obj, 'projectSubcategories', 'entities');
  const platforms = requireArray(obj, 'platforms', 'entities');
  if (platforms.length === 0) {
    throw new ThemeValidationError('entities.platforms', 'Must have at least one platform');
  }

  const marketingStrategies = requireArray(obj, 'marketingStrategies', 'entities');
  const competitors = requireArray(obj, 'competitors', 'entities');
  const trainingOptions = requireArray(obj, 'trainingOptions', 'entities');
  const traits = requireArray(obj, 'traits', 'entities');
  const offices = requireArray(obj, 'offices', 'entities');
  if (offices.length === 0) {
    throw new ThemeValidationError('entities.offices', 'Must have at least one office level');
  }

  const seniorRoles = Array.isArray((obj as Record<string, unknown>)['seniorRoles']) ? (obj as Record<string, unknown>)['seniorRoles'] as unknown[] : [];
  const recruiters = Array.isArray((obj as Record<string, unknown>)['recruiters']) ? (obj as Record<string, unknown>)['recruiters'] as unknown[] : [];
  const equipment = Array.isArray((obj as Record<string, unknown>)['equipment']) ? (obj as Record<string, unknown>)['equipment'] as unknown[] : [];
  const perkEffects = typeof (obj as Record<string, unknown>)['perkEffects'] === 'object' ? (obj as Record<string, unknown>)['perkEffects'] as Record<string, unknown> : {};
  const categoryComboAffinity = requireObject(obj, 'categoryComboAffinity', 'entities');

  // Validate stat weights sum to ~1.0 for each category
  for (const cat of projectCategories) {
    const c = cat as Record<string, unknown>;
    const weights = c['statWeights'] as Record<string, number> | undefined;
    if (weights) {
      const sum = Object.values(weights).reduce((a, b) => a + b, 0);
      if (Math.abs(sum - 1.0) > 0.01) {
        throw new ThemeValidationError(
          `entities.projectCategories.${c['id']}`,
          `Stat weights must sum to 1.0, got ${sum.toFixed(3)}`,
        );
      }
    }
  }

  return {
    staffRoles: staffRoles as ThemeEntities['staffRoles'],
    founderRole: founderRole as unknown as ThemeEntities['founderRole'],
    projectCategories: projectCategories as ThemeEntities['projectCategories'],
    projectSubcategories: projectSubcategories as ThemeEntities['projectSubcategories'],
    platforms: platforms as ThemeEntities['platforms'],
    marketingStrategies: marketingStrategies as ThemeEntities['marketingStrategies'],
    competitors: competitors as ThemeEntities['competitors'],
    trainingOptions: trainingOptions as ThemeEntities['trainingOptions'],
    traits: traits as ThemeEntities['traits'],
    offices: offices as ThemeEntities['offices'],
    categoryComboAffinity: categoryComboAffinity as Record<string, number>,
    seniorRoles: seniorRoles as ThemeEntities['seniorRoles'],
    recruiters: recruiters as ThemeEntities['recruiters'],
    equipment: equipment as ThemeEntities['equipment'],
    perkEffects: perkEffects as ThemeEntities['perkEffects'],
  };
}

function validateBalance(raw: unknown): ThemeBalance {
  if (typeof raw !== 'object' || raw === null) {
    throw new ThemeValidationError('balance', 'Balance must be an object');
  }
  const obj = raw as Record<string, unknown>;

  // Validate required numeric fields
  const numericFields = [
    'startingFunds',
    'ticksPerDay',
    'daysPerWeek',
    'weeksPerYear',
    'campaignYears',
    'xpBase',
    'xpGrowthRate',
    'maxStaffLevel',
    'energyDrainPerTick',
    'energyRecoveryPerTick',
    'moraleDrainPerTick',
    'demandSatisfactionThreshold',
    'demandCooldownWeeks',
    'demandResponseDays',
    'departureNoticeDays',
    'bugBaseRate',
    'sequelQualityBonus',
    'reviewVariance',
    'hallOfFameThreshold',
    'salesDecayRate',
    'salesDurationWeeks',
    'gameOverDebtWeeks',
    'debtInterestRate',
    'hiringPoolSize',
    'hiringPoolRefreshWeeks',
  ] as const;

  for (const field of numericFields) {
    requireNumber(obj, field, 'balance');
  }

  // Validate projectTicks has all sizes
  const projectTicks = requireObject(obj, 'projectTicks', 'balance');
  const sizes: ProjectSize[] = ['small', 'medium', 'large', 'aaa'];
  for (const size of sizes) {
    requireNumber(projectTicks, size, 'balance.projectTicks');
  }

  // Validate hard mode and prestige sub-objects exist
  requireObject(obj, 'hardMode', 'balance');
  requireObject(obj, 'prestige', 'balance');

  // Sanity checks
  const startingFunds = obj['startingFunds'] as number;
  if (startingFunds <= 0) {
    throw new ThemeValidationError('balance.startingFunds', 'Must be positive');
  }
  const xpGrowthRate = obj['xpGrowthRate'] as number;
  if (xpGrowthRate <= 1.0) {
    throw new ThemeValidationError('balance.xpGrowthRate', 'Must be greater than 1.0');
  }

  return raw as ThemeBalance;
}

function validateNames(raw: unknown): ThemeNames {
  if (typeof raw !== 'object' || raw === null) {
    throw new ThemeValidationError('names', 'Names must be an object');
  }
  const obj = raw as Record<string, unknown>;

  const staffNames = requireObject(obj, 'staffNames', 'names');
  const firstNames = requireArray(staffNames, 'firstNames', 'names.staffNames');
  const lastNames = requireArray(staffNames, 'lastNames', 'names.staffNames');

  if (firstNames.length < 20) {
    throw new ThemeValidationError(
      'names.staffNames.firstNames',
      `Need at least 20 first names, got ${firstNames.length}`,
    );
  }
  if (lastNames.length < 20) {
    throw new ThemeValidationError(
      'names.staffNames.lastNames',
      `Need at least 20 last names, got ${lastNames.length}`,
    );
  }

  const projectNameParts = requireObject(obj, 'projectNameParts', 'names');
  requireArray(projectNameParts, 'prefixes', 'names.projectNameParts');
  requireArray(projectNameParts, 'nouns', 'names.projectNameParts');
  requireArray(projectNameParts, 'suffixes', 'names.projectNameParts');

  requireArray(obj, 'namePatterns', 'names');
  requireArray(obj, 'contractClients', 'names');

  return raw as ThemeNames;
}

function validateStrings(raw: unknown): Record<string, string> {
  if (typeof raw !== 'object' || raw === null) {
    throw new ThemeValidationError('strings', 'Strings must be an object');
  }

  const obj = raw as Record<string, unknown>;

  // Check that all values are strings
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value !== 'string') {
      throw new ThemeValidationError(`strings.${key}`, `Value must be a string, got ${typeof value}`);
    }
  }

  // Check for critical required strings
  const requiredKeys = [
    'nouns.project',
    'nouns.staff',
    'nouns.company',
    'ui.hud.funds',
    'ui.hud.year',
  ];

  for (const key of requiredKeys) {
    if (!(key in obj)) {
      throw new ThemeValidationError('strings', `Missing required string key '${key}'`);
    }
  }

  return raw as Record<string, string>;
}
